import { EyeIcon, PencilAltIcon } from '@heroicons/react/outline';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * The apps an agent may look in before it answers an email, all read-only,
 * and the draft it saved after looking, held for the owner's review.
 */
export default function CustomerDataApps({
  subject,
  apps,
}: {
  subject: string;
  apps: { name: string; mark: string; color: string }[];
}) {
  const { t } = useChannelsT();

  return (
    <div className='relative mx-auto max-w-[30rem] pb-2 sm:pb-20'>
      <div
        className={clsxm(
          'overflow-hidden rounded-[22px] p-5 text-start shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)]',
          tone.surface,
          tone.line,
        )}
      >
        <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
          {t('Customer data apps')}
        </p>
        <p className={clsxm('mt-1 text-xs', tone.quiet)}>
          {t('Before replying, the agent can check')}
        </p>
        <ul className='mt-4 space-y-2'>
          {apps.map(({ name, mark, color }) => (
            <li
              key={name}
              className='flex items-center gap-3 rounded-xl px-3 py-2.5 ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/10'
            >
              <span
                className={clsxm(
                  'flex h-8 w-8 flex-none items-center justify-center rounded-lg text-[13px] font-bold text-white',
                  color,
                )}
              >
                {mark}
              </span>
              <span
                className={clsxm(
                  'min-w-0 flex-1 text-[13.5px] font-semibold',
                  tone.ink,
                )}
              >
                {name}
              </span>
              <span className='flex flex-none items-center gap-1 rounded-full bg-[#f1f1f5] px-2.5 py-1 text-[11px] font-semibold text-[#56566d] dark:bg-white/10 dark:text-[#a4a4bd]'>
                <EyeIcon className='h-3.5 w-3.5' />
                {t('read-only')}
              </span>
              <span className='flex h-5 w-5 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
                <CheckGlyph className='h-3 w-3' />
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[19rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p className='flex items-center gap-2'>
          <span className='flex h-8 w-8 flex-none items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300'>
            <PencilAltIcon className='h-4 w-4' />
          </span>
          <span className='min-w-0'>
            <span
              className={clsxm(
                'block truncate text-[13px] font-semibold',
                tone.ink,
              )}
            >
              {subject}
            </span>
            <span className={clsxm('block text-xs', tone.quiet)}>
              {t('Draft saved for your review')}
            </span>
          </span>
        </p>
        <p className={clsxm('mt-3 text-xs', tone.body)}>
          {t('Looked up {{apps}}', { apps: apps[0]?.name ?? '' })}
        </p>
      </div>
    </div>
  );
}
